import { FC, Fragment } from 'react';
import { DisputeListPreset } from '../../disputes.model';
import { DisputeVisibility } from '../disputeVisibility.model';
import { parseTaggedText } from '../taggedText';
import {
  buildInReviewPreset,
  buildLostPreset,
  buildNeedsResponsePreset,
  buildPresetFromDisputeFilter,
  buildWonPreset,
  resolveDisputeWindowRange
} from '../utils';
import { DisputeChevron } from './DisputeZoneChip';
import DisputeFunnelRow from './DisputeFunnelRow';
import DisputeSectionHeader from './DisputeSectionHeader';
import { DisputeTaggedText } from './DisputeTaggedText';

interface DisputeFunnelSectionProps {
  summary: DisputeVisibility;
  onOpenList: (preset: DisputeListPreset, action: string) => void;
}

const reasonLineAction = (text: string) => {
  const plain = parseTaggedText(text)
    .map(span => span.text)
    .join('')
    .trim();
  return plain || 'reason_line';
};

const DisputeFunnelSection: FC<DisputeFunnelSectionProps> = ({ summary, onOpenList }) => {
  const funnel = summary.funnel;
  const range = resolveDisputeWindowRange(90, funnel.windowStart, funnel.windowEnd);
  const rangeStart = range.start;
  const rangeEnd = range.end;

  const rows: {
    label: string;
    count: number;
    subtitle?: string;
    showChevron?: boolean;
    onClick?: () => void;
  }[] = [
    {
      label: 'Awaiting response',
      count: funnel.awaitingResponse,
      showChevron: true,
      onClick:
        funnel.awaitingResponse > 0
          ? () => onOpenList(buildNeedsResponsePreset(rangeStart, rangeEnd), 'Awaiting response')
          : undefined
    },
    {
      label: 'Won',
      count: funnel.won,
      onClick: funnel.won > 0 ? () => onOpenList(buildWonPreset(rangeStart, rangeEnd), 'Won') : undefined
    },
    {
      label: 'Lost',
      count: funnel.lost,
      subtitle: funnel.notContestedLine,
      onClick: funnel.lost > 0 ? () => onOpenList(buildLostPreset(rangeStart, rangeEnd), 'Lost') : undefined
    },
    {
      label: 'In review',
      count: funnel.inReview,
      onClick:
        funnel.inReview > 0 ? () => onOpenList(buildInReviewPreset(rangeStart, rangeEnd), 'In review') : undefined
    }
  ];

  return (
    <section>
      <DisputeSectionHeader
        text={
          <>
            Disputes · <DisputeTaggedText text={funnel.header} />
          </>
        }
      />
      <div className="overflow-hidden rounded-md border border-borderGray py-4">
        {summary.reasonLines.map((line, index) => {
          const content = <DisputeTaggedText text={line.text} />;
          const filter = line.filter;
          if (!filter) {
            return (
              <p key={`${line.text}-${index}`} className="mb-1 px-4 text-px13 text-primaryText">
                {content}
              </p>
            );
          }

          const handleClick = () => {
            onOpenList(buildPresetFromDisputeFilter(filter), reasonLineAction(line.text));
          };

          return (
            <button
              key={`${line.text}-${index}`}
              type="button"
              className="mb-1 flex items-center gap-2 px-4 text-left text-px13 text-primaryText"
              onClick={handleClick}>
              <span>{content}</span>
              <DisputeChevron />
            </button>
          );
        })}
        {rows.map((row, index) => (
          <Fragment key={row.label}>
            {index > 0 || summary.reasonLines.length ? <div className="mr-9 ml-4 h-px bg-secondaryBtn" /> : null}
            <DisputeFunnelRow
              label={row.label}
              count={row.count}
              subtitle={row.subtitle}
              onClick={row.onClick}
              showChevron={row.showChevron}
            />
          </Fragment>
        ))}
      </div>
    </section>
  );
};

export default DisputeFunnelSection;
